// What the institution computed, against what it declared and what it published.
//
// Three independent facts about a cube — declared in aggregations.json, computed
// as a .ttl in the private aggregates container, published into a partner's
// outbound container — and until now the app rendered every absence the same
// way. A cube that was never computed, one deliberately kept private, and one
// whose publish never happened all read as a blank space. Naming the states is
// the whole point: two of them only ever appear after something went wrong.
//
// Pure, and in lib/ rather than web/server/ for the same reason as
// disclosure_writes.js: tests import it without pulling in auth.js or
// shared/config.js import-time side effects. The Pod reading lives in
// web/server/cubes.js on top of this; reshapeCells is here too, because
// "what the .ttl actually carries" is the half of the comparison that must not
// be derived from the config.

const RDF_TYPE = 'http://www.w3.org/1999/02/22-rdf-syntax-ns#type';

// One phrase per state, single-sourced so the Catalog and the tests cannot
// drift. `tone` maps to the app's existing state classes; `needsHuman` marks the
// two that are nobody's intended outcome.
export const CUBE_STATES = {
  published: {
    label: 'Shared',
    tone: 'ok',
    note: 'Counted here, and a partner has a copy.',
  },
  'private-by-intent': {
    label: 'Kept private',
    tone: 'ok',
    note: 'Counted here and shared with nobody, because that is what you asked for. This is a normal outcome, not something missing.',
  },
  'publish-pending': {
    label: 'Waiting to be shared',
    tone: 'warn',
    note: 'Counted here and meant to be shared, but no partner has a copy yet. The governance owner sends it.',
  },
  'not-computed': {
    label: 'Not counted yet',
    tone: 'warn',
    note: 'You asked for this cube, but it has never been counted — nothing has run since you added it.',
  },
  orphan: {
    label: 'Left over',
    tone: 'error',
    note: 'A cube file that nothing asked for — what a failed removal leaves behind. It will never be counted again, so its numbers will quietly go stale.',
    needsHuman: true,
  },
  'published-beyond-intent': {
    label: 'Shared when it shouldn’t be',
    tone: 'error',
    note: 'A partner has a copy of this, but you asked for it to be shared with nobody. Either take it back or change what you asked for.',
    needsHuman: true,
  },
};

// Precedence, most alarming first, so every combination lands somewhere:
//   - published while intent says none outranks everything, including an orphan
//     that reached a partner (no declaration is no intent to publish, and the
//     copy already left);
//   - an orphan that stayed private is next;
//   - after that the ordinary declared/computed/published lattice.
function stateOf({ declared, computed, published, mode }) {
  if (published && mode === 'none') return 'published-beyond-intent';
  if (!declared) return 'orphan';
  if (!computed) return 'not-computed';
  if (mode === 'none') return 'private-by-intent';
  return published ? 'published' : 'publish-pending';
}

// aggregations       — the aggregations.json document
// computedIds        — cube ids found in the private aggregates container
// publishedByPartner — [{ institution, label, cubeIds }] per partner container
//
// Ordering is the analyst's own: declared order first (that is the order they
// authored the file in), orphans appended, since an orphan has no place in it.
export function reconcileCubes({ aggregations = {}, computedIds = [], publishedByPartner = [] } = {}) {
  const declaredList = aggregations.aggregates ?? [];
  const computed = new Set(computedIds);

  // De-duplicate by partner, not by filename: the same cube is published to
  // every partner granted its dataset, and each copy is a separate fact about
  // where it went — but one partner listing it twice is not two partners.
  const whereItWent = new Map();
  for (const p of publishedByPartner) {
    for (const id of new Set(p.cubeIds ?? [])) {
      const seen = whereItWent.get(id) ?? new Map();
      seen.set(p.institution ?? p.label, { institution: p.institution ?? null, label: p.label ?? p.institution ?? '' });
      whereItWent.set(id, seen);
    }
  }
  const publishedTo = (id) => [...(whereItWent.get(id)?.values() ?? [])];

  const row = (id, declaration) => {
    const to = publishedTo(id);
    const mode = declaration ? (declaration.sharing?.mode ?? 'none') : 'none';
    return {
      id,
      dataset: declaration?.dataset ?? null,
      // The declared axes. What the .ttl actually carries is read from the Pod
      // and reported separately — the divergence is a thing worth seeing.
      dimensions: declaration?.dimensions ?? [],
      measure: declaration?.measure ?? aggregations.measure ?? null,
      reducer: declaration?.reducer ?? 'count',
      sharing: declaration?.sharing ?? null,
      mode,
      declared: Boolean(declaration),
      computed: computed.has(id),
      publishedTo: to,
      state: stateOf({ declared: Boolean(declaration), computed: computed.has(id), published: to.length > 0, mode }),
    };
  };

  const rows = declaredList.map((a) => row(a.id, a));
  const declaredIds = new Set(declaredList.map((a) => a.id));
  for (const id of computedIds) if (!declaredIds.has(id)) rows.push(row(id, null));

  // A cube published by a partner that this institution never declared or
  // computed is not this institution's cube at all — it is not reported here.
  return rows;
}

// One cube's triples → the cells it actually holds.
//
// Read generically (?obs ?p ?o) and classified here rather than queried from the
// declared dimensions, because the config is the other half of the comparison:
// a cube carrying an axis nobody declared has to be visible, and it cannot be if
// the query only asks for what was declared. Anything that is not the type, the
// measure, the period, or a structural predicate is a dimension.
export function reshapeCells(triples, { period, measure, ignore = [] } = {}) {
  const structural = new Set([RDF_TYPE, ...ignore]);
  const byObs = new Map();

  for (const { obs, p, o } of triples) {
    if (!byObs.has(obs)) byObs.set(obs, { values: {}, period: null, count: 0 });
    const cell = byObs.get(obs);
    if (p === period) cell.period = o;
    else if (p === measure) cell.count = Number(o) || 0;
    else if (!structural.has(p)) cell.values[p] = o;
  }

  const cells = [...byObs.values()];
  // Sorted, not first-seen: SPARQL results carry no guaranteed triple order, so
  // first-seen would make the column order of a rendered cube depend on how the
  // engine felt that morning.
  const dimensions = [...new Set(cells.flatMap((c) => Object.keys(c.values)))].sort();

  // Values aligned to `dimensions`, so a cell is a table row. A cube whose
  // observations don't all carry the same axes leaves a hole rather than
  // shifting the row — a hole is the honest rendering of an axis this
  // observation does not have.
  const align = (values) => dimensions.map((d) => values[d] ?? null);

  // Periods ascending — they are quarters, and reading them out of order is
  // never what anyone wants. Cells within a period by count descending, the
  // same order the cube itself was written in.
  const byPeriod = new Map();
  for (const cell of cells) {
    const key = cell.period ?? '';
    if (!byPeriod.has(key)) byPeriod.set(key, []);
    byPeriod.get(key).push({ values: align(cell.values), count: cell.count });
  }
  const periods = [...byPeriod.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([period, group]) => ({
      period: period || null,
      total: group.reduce((s, c) => s + c.count, 0),
      cells: group.sort((a, b) => b.count - a.count),
    }));

  return { observations: cells.length, dimensions, periods };
}
